// Configuración › Camiones, Choferes y Rutas (docs/110). Mantenedores densos: la grilla a la izquierda
// y el formulario de alta/edición al lado. El camión se asocia a una ubicación CAMION del inventario
// (su stock es el del camión en el libro).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/cliente';
import { useUbicaciones } from '../api/consultas';
import { PanelError } from '../comun/Estados';
import { Grilla, type Columna } from '../comun/Grilla';
import { useSesion } from '../sesion/SesionContexto';
import {
  clavesLog,
  useChoferes,
  useRutas,
  useVehiculos,
  NOMBRE_ROL_PERSONAL,
  type Chofer,
  type RolPersonalRuta,
  type Ruta,
  type Vehiculo,
} from './datos';

type Tipo = 'camiones' | 'choferes' | 'rutas';

export function MaestrosLogistica({ tipo }: { tipo: Tipo }) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const gestiona = puede('logistica.maestros.gestionar');
  return (
    <div className="pantalla consulta" data-pantalla={`maestro-${tipo}`}>
      {!gestiona && (
        <p className="aviso-linea tono-neutro">
          Solo consulta: editar requiere el permiso «Crear y editar camiones, choferes y rutas».
        </p>
      )}
      {tipo === 'camiones' && <Camiones empresaId={empresaId} gestiona={gestiona} />}
      {tipo === 'choferes' && <Choferes empresaId={empresaId} gestiona={gestiona} />}
      {tipo === 'rutas' && <Rutas empresaId={empresaId} gestiona={gestiona} />}
    </div>
  );
}

function useGuardar(empresaId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      metodo,
      ruta,
      cuerpo,
    }: {
      metodo: 'post' | 'patch' | 'put';
      ruta: string;
      cuerpo: Record<string, unknown>;
    }) => api[metodo](ruta, { empresaId, ...cuerpo }),
    onSuccess: () => qc.invalidateQueries({ queryKey: clavesLog.logistica(empresaId) }),
  });
}

function Camiones({ empresaId, gestiona }: { empresaId: string; gestiona: boolean }) {
  const q = useVehiculos(empresaId);
  const ubicaciones = useUbicaciones(empresaId);
  const camiones = (ubicaciones.data ?? []).filter((u) => u.tipo === 'CAMION');
  const guardar = useGuardar(empresaId);
  const [patente, setPatente] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [ubicacionId, setUbicacion] = useState('');
  const [capacidad, setCapacidad] = useState('');
  const libres = camiones.filter((c) => !(q.data ?? []).some((v) => v.ubicacionId === c.id));
  const columnas: Columna<Vehiculo>[] = [
    {
      id: 'patente',
      titulo: 'Patente',
      celda: (v) => <span className="mono">{v.patente}</span>,
      ancho: '7rem',
    },
    { id: 'desc', titulo: 'Descripción', celda: (v) => v.descripcion },
    {
      id: 'ubic',
      titulo: 'Ubicación de inventario',
      celda: (v) => camiones.find((c) => c.id === v.ubicacionId)?.codigo ?? '—',
      ancho: '10rem',
    },
    {
      id: 'cap',
      titulo: 'Capacidad kg',
      celda: (v) => v.capacidadKg ?? '—',
      alinear: 'der',
      ancho: '8rem',
    },
    {
      id: 'activo',
      titulo: 'Activo',
      celda: (v) =>
        gestiona ? (
          <input
            type="checkbox"
            aria-label={`Activo ${v.patente}`}
            checked={v.activo}
            onChange={(e) =>
              guardar.mutate({
                metodo: 'patch',
                ruta: `/logistica/vehiculos/${v.id}`,
                cuerpo: { activo: e.target.checked },
              })
            }
          />
        ) : v.activo ? (
          'Sí'
        ) : (
          'No'
        ),
      ancho: '5rem',
    },
  ];
  return (
    <>
      {gestiona && (
        <form
          className="formulario-linea"
          aria-label="Nuevo camión"
          onSubmit={(e) => {
            e.preventDefault();
            guardar.mutate(
              {
                metodo: 'post',
                ruta: '/logistica/vehiculos',
                cuerpo: {
                  patente,
                  descripcion,
                  ubicacionId: ubicacionId || libres[0]?.id,
                  capacidadKg: capacidad || null,
                },
              },
              {
                onSuccess: () => {
                  setPatente('');
                  setDescripcion('');
                  setCapacidad('');
                },
              },
            );
          }}
        >
          <label className="campo campo-corto">
            <span className="etiqueta">Patente</span>
            <input
              aria-label="Patente"
              value={patente}
              onChange={(e) => setPatente(e.target.value.toUpperCase())}
            />
          </label>
          <label className="campo">
            <span className="etiqueta">Descripción</span>
            <input
              aria-label="Descripción del camión"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
            />
          </label>
          <label className="campo">
            <span className="etiqueta">Ubicación CAMION</span>
            <select
              aria-label="Ubicación del camión"
              value={ubicacionId}
              onChange={(e) => setUbicacion(e.target.value)}
            >
              {libres.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.codigo} · {c.nombre}
                </option>
              ))}
            </select>
          </label>
          <label className="campo campo-corto">
            <span className="etiqueta">Capacidad kg</span>
            <input
              aria-label="Capacidad en kg"
              inputMode="decimal"
              value={capacidad}
              onChange={(e) => setCapacidad(e.target.value)}
            />
          </label>
          <button
            type="submit"
            className="primario"
            disabled={!patente || !descripcion || !libres.length || guardar.isPending}
          >
            Agregar camión
          </button>
          {!libres.length && (
            <span className="apagado">
              No hay ubicaciones tipo CAMION libres: créalas en Bodegas (inventario) y clasifícalas
              como camión.
            </span>
          )}
        </form>
      )}
      {guardar.isError && <PanelError error={guardar.error} />}
      <Grilla
        etiqueta="Camiones"
        columnas={columnas}
        filas={q.data ?? []}
        clave={(v) => v.id}
        cargando={q.isLoading}
        vacio="Sin camiones"
      />
    </>
  );
}

function Choferes({ empresaId, gestiona }: { empresaId: string; gestiona: boolean }) {
  const q = useChoferes(empresaId);
  const { puede } = useSesion();
  // docs/111 · el usuario del ERP con que el peoneta entra a la app (#/ruta). La lista de usuarios
  // la ve quien administra usuarios; los demás ven solo si está ligado.
  const usuarios = useQuery({
    queryKey: ['usuarios', 'para-ruta'],
    queryFn: () =>
      api.get<Array<{ id: string; nombre: string; correo: string; estado: string }>>('/usuarios'),
    enabled: gestiona && puede('organizacion.usuarios.ver'),
  });
  const guardar = useGuardar(empresaId);
  const [rut, setRut] = useState('');
  const [nombre, setNombre] = useState('');
  const [telefono, setTelefono] = useState('');
  const [licencia, setLicencia] = useState('');
  const [rol, setRol] = useState<RolPersonalRuta>('CHOFER');
  const limpio = rut.replace(/[^\dkK]/g, '').toUpperCase();
  const cuerpoRut = limpio.slice(0, -1);
  const dv = limpio.slice(-1);
  const columnas: Columna<Chofer>[] = [
    {
      id: 'rut',
      titulo: 'RUT',
      celda: (c) => <span className="mono">{`${c.rut}-${c.dv}`}</span>,
      ancho: '9rem',
    },
    { id: 'nombre', titulo: 'Nombre', celda: (c) => c.nombre },
    {
      id: 'rol',
      titulo: 'Rol',
      celda: (c) =>
        gestiona ? (
          <select
            aria-label={`Rol de ${c.nombre}`}
            value={c.rol ?? 'CHOFER'}
            onChange={(e) =>
              guardar.mutate({
                metodo: 'patch',
                ruta: `/logistica/choferes/${c.id}`,
                cuerpo: { rol: e.target.value },
              })
            }
          >
            {Object.entries(NOMBRE_ROL_PERSONAL).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        ) : (
          NOMBRE_ROL_PERSONAL[c.rol ?? 'CHOFER']
        ),
      ancho: '12rem',
    },
    {
      id: 'usuario',
      titulo: 'Usuario app de ruta',
      celda: (c) =>
        usuarios.data ? (
          <select
            aria-label={`Usuario de ${c.nombre}`}
            value={c.usuarioId ?? ''}
            onChange={(e) =>
              guardar.mutate({
                metodo: 'patch',
                ruta: `/logistica/choferes/${c.id}`,
                cuerpo: { usuarioId: e.target.value || null },
              })
            }
          >
            <option value="">— rinde a mano —</option>
            {usuarios.data
              .filter((u) => u.estado === 'activo' || u.id === c.usuarioId)
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.nombre} · {u.correo}
                </option>
              ))}
          </select>
        ) : c.usuarioId ? (
          'Ligado'
        ) : (
          '—'
        ),
      ancho: '16rem',
    },
    { id: 'tel', titulo: 'Teléfono', celda: (c) => c.telefono ?? '—', ancho: '9rem' },
    { id: 'lic', titulo: 'Licencia', celda: (c) => c.licencia ?? '—', ancho: '7rem' },
    {
      id: 'activo',
      titulo: 'Activo',
      celda: (c) =>
        gestiona ? (
          <input
            type="checkbox"
            aria-label={`Activo ${c.nombre}`}
            checked={c.activo}
            onChange={(e) =>
              guardar.mutate({
                metodo: 'patch',
                ruta: `/logistica/choferes/${c.id}`,
                cuerpo: { activo: e.target.checked },
              })
            }
          />
        ) : c.activo ? (
          'Sí'
        ) : (
          'No'
        ),
      ancho: '5rem',
    },
  ];
  return (
    <>
      {gestiona && (
        <form
          className="formulario-linea"
          aria-label="Nueva persona de ruta"
          onSubmit={(e) => {
            e.preventDefault();
            guardar.mutate(
              {
                metodo: 'post',
                ruta: '/logistica/choferes',
                cuerpo: {
                  rut: cuerpoRut,
                  dv,
                  nombre,
                  telefono: telefono || null,
                  licencia: licencia || null,
                  rol,
                },
              },
              {
                onSuccess: () => {
                  setRut('');
                  setNombre('');
                  setTelefono('');
                  setLicencia('');
                  setRol('CHOFER');
                },
              },
            );
          }}
        >
          <label className="campo campo-corto">
            <span className="etiqueta">RUT</span>
            <input
              aria-label="RUT del chofer"
              placeholder="12345678-9"
              value={rut}
              onChange={(e) => setRut(e.target.value)}
            />
          </label>
          <label className="campo">
            <span className="etiqueta">Rol</span>
            <select
              aria-label="Rol"
              value={rol}
              onChange={(e) => setRol(e.target.value as RolPersonalRuta)}
            >
              {Object.entries(NOMBRE_ROL_PERSONAL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label className="campo">
            <span className="etiqueta">Nombre</span>
            <input
              aria-label="Nombre del chofer"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
            />
          </label>
          <label className="campo campo-corto">
            <span className="etiqueta">Teléfono</span>
            <input
              aria-label="Teléfono"
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
            />
          </label>
          <label className="campo campo-corto">
            <span className="etiqueta">Licencia</span>
            <input
              aria-label="Licencia"
              value={licencia}
              onChange={(e) => setLicencia(e.target.value)}
            />
          </label>
          <button
            type="submit"
            className="primario"
            disabled={cuerpoRut.length < 6 || nombre.trim().length < 3 || guardar.isPending}
          >
            Agregar
          </button>
        </form>
      )}
      {guardar.isError && <PanelError error={guardar.error} />}
      <Grilla
        etiqueta="Personal de ruta"
        columnas={columnas}
        filas={q.data ?? []}
        clave={(c) => c.id}
        cargando={q.isLoading}
        vacio="Todavía no hay choferes ni peonetas"
      />
    </>
  );
}

function Rutas({ empresaId, gestiona }: { empresaId: string; gestiona: boolean }) {
  const q = useRutas(empresaId);
  const zonas = useQuery({
    queryKey: ['emp', empresaId, 'zonas'],
    queryFn: () =>
      api.get<Array<{ id: string; nombre: string }>>('/ventas/zonas', { query: { empresaId } }),
  });
  const guardar = useGuardar(empresaId);
  const [editando, setEditando] = useState<Ruta | null>(null);
  const [codigo, setCodigo] = useState('');
  const [nombre, setNombre] = useState('');
  const [zonaIds, setZonaIds] = useState<string[]>([]);
  const nombreZona = new Map((zonas.data ?? []).map((z) => [z.id, z.nombre]));
  const editar = (r: Ruta | null) => {
    setEditando(r);
    setCodigo(r?.codigo ?? '');
    setNombre(r?.nombre ?? '');
    setZonaIds(r?.zonaIds ?? []);
  };
  const columnas: Columna<Ruta>[] = [
    {
      id: 'cod',
      titulo: 'Código',
      celda: (r) => <span className="mono">{r.codigo}</span>,
      ancho: '7rem',
    },
    { id: 'nombre', titulo: 'Nombre', celda: (r) => r.nombre },
    {
      id: 'zonas',
      titulo: 'Zonas',
      celda: (r) => r.zonaIds.map((z) => nombreZona.get(z) ?? '?').join(', ') || '—',
    },
    { id: 'activo', titulo: 'Activa', celda: (r) => (r.activo ? 'Sí' : 'No'), ancho: '5rem' },
  ];
  return (
    <>
      {gestiona && (
        <form
          className="formulario-linea"
          aria-label={editando ? `Editar ruta ${editando.codigo}` : 'Nueva ruta'}
          onSubmit={(e) => {
            e.preventDefault();
            guardar.mutate(
              {
                metodo: 'put',
                ruta: '/logistica/rutas',
                cuerpo: { ...(editando ? { rutaId: editando.id } : {}), codigo, nombre, zonaIds },
              },
              { onSuccess: () => editar(null) },
            );
          }}
        >
          <label className="campo campo-corto">
            <span className="etiqueta">Código</span>
            <input
              aria-label="Código de la ruta"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.toUpperCase())}
            />
          </label>
          <label className="campo">
            <span className="etiqueta">Nombre</span>
            <input
              aria-label="Nombre de la ruta"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
            />
          </label>
          <fieldset className="campo zonas-ruta">
            <legend className="etiqueta">Zonas</legend>
            {(zonas.data ?? []).map((z) => (
              <label key={z.id} className="campo-check">
                <input
                  type="checkbox"
                  checked={zonaIds.includes(z.id)}
                  onChange={(e) =>
                    setZonaIds((x) =>
                      e.target.checked ? [...x, z.id] : x.filter((y) => y !== z.id),
                    )
                  }
                />{' '}
                {z.nombre}
              </label>
            ))}
          </fieldset>
          <button
            type="submit"
            className="primario"
            disabled={!codigo || nombre.trim().length < 2 || guardar.isPending}
          >
            {editando ? 'Guardar ruta' : 'Agregar ruta'}
          </button>
          {editando && (
            <button type="button" onClick={() => editar(null)}>
              Cancelar
            </button>
          )}
        </form>
      )}
      {guardar.isError && <PanelError error={guardar.error} />}
      <Grilla
        etiqueta="Rutas"
        columnas={columnas}
        filas={q.data ?? []}
        clave={(r) => r.id}
        cargando={q.isLoading}
        vacio="Sin rutas"
        {...(gestiona ? { alAbrir: editar } : {})}
      />
    </>
  );
}
